import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';
import debounce from 'lodash/debounce';

import { ISettingsStore } from 'shared/entities/store/settingsStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import {
  EventsQueueMode,
  areCsatSettingsEqual,
  CsatSettingsServer,
  mapUserCardVarErrorToMessage,
  normalizeCsatSettings,
  normalizeUserVars,
  ProjectSettingsServer,
  UnstopDelay,
  UserVars
} from 'shared/entities/projectSettings';
import { LoadingStage } from 'shared/entities/meta';
import { apiUrls } from 'shared/entities/domain';
import {
  EnabledProjectSettingModel,
  ModeProjectModel,
  CsatSettingsModel,
  BusinessHoursModel
} from 'shared/models/projectSettings';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { SettingsType } from 'shared/entities/projectSettingsTypes';
import { ComponentLoadingStore } from 'stores/componentLoadingStore';
import { FieldModel } from 'shared/models/form';
import { TranslationString } from 'shared/entities/localization';
import { LoadingStageModel } from 'shared/models/loadingStage';

const DEBOUNCED_LIKE_THRESHOLD_DELAY = 200;

export default class SettingsStore
  extends ComponentLoadingStore
  implements ISettingsStore
{
  rootStore: IRootStore;
  loadingStage: LoadingStage = LoadingStage.NOT_STARTED;
  updatingStage: LoadingStage = LoadingStage.NOT_STARTED;

  debugMode: ModeProjectModel | null = null;
  useUnpublished: ModeProjectModel | null = null;
  enabledProjectSetting: EnabledProjectSettingModel | null = null;
  likeThreshold: number | null = null;
  timezone: string | null = null;
  eventsQueueMode: EventsQueueMode | null = null;
  conversationAutoCloseDelayMinutes: number | null = null;
  readonly conversationAutoCloseUpdatingStage = new LoadingStageModel();
  userVars: UserVars = {
    email: null,
    phone: null
  };
  userCardPhoneVar: string | null = null;
  unstopByManagerDelay: FieldModel<UnstopDelay | null> = new FieldModel(null);
  csat: CsatSettingsModel | null = null;
  readonly businessHours: BusinessHoursModel;

  constructor(rootStore: IRootStore) {
    super();

    makeObservable(this, {
      loadingStage: observable,
      updatingStage: observable,
      debugMode: observable,
      useUnpublished: observable,
      likeThreshold: observable,
      enabledProjectSetting: observable,
      timezone: observable,
      eventsQueueMode: observable,
      conversationAutoCloseDelayMinutes: observable,
      conversationAutoCloseUpdating: computed,
      userVars: observable,
      csat: observable,

      canSaveCsat: computed,

      updateThreshold: action,
      updateTimezone: action,
      updateUserCardEmailVar: action,
      updateUserCardPhoneVar: action,
      update: action,
      updateEventsQueueMode: action,
      updateCsat: action,
      updateCsatEnabled: action,
      updateCsatAskComment: action,
      resetCsatDraft: action,
      updateConversationAutoCloseDelayMinutes: action
    });
    this.rootStore = rootStore;
    this.businessHours = new BusinessHoursModel(rootStore);
  }

  get loaded(): boolean {
    return this.loadingStage === LoadingStage.SUCCESS;
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized;
  }

  get canSaveCsat(): boolean {
    return !!this.csat?.canSave && this.updatingStage !== LoadingStage.LOADING;
  }

  get conversationAutoCloseUpdating(): boolean {
    return this.conversationAutoCloseUpdatingStage.isLoading;
  }

  private debouncedLikeThreshold = debounce(async (preValue: number) => {
    const { isError } = await this.update(
      { [`${SettingsType.likeThreshold}`]: this.likeThreshold },
      (t) =>
        t('SettingsStore.notifications.likeThresholdError', {
          ns: 'stores'
        })
    );
    if (isError) {
      this.likeThreshold = preValue;
    } else {
      this.rootStore.appNotificationsStore.open({
        title: (t) =>
          t('SettingsStore.notifications.likeThresholdSuccess', {
            ns: 'stores'
          }),
        type: AppNotificationType.success
      });
    }
  }, DEBOUNCED_LIKE_THRESHOLD_DELAY);

  updateEventsQueueMode = async (value: EventsQueueMode) => {
    const prevValue = this.eventsQueueMode;
    this.eventsQueueMode = value;
    const { isError } = await this.update(
      { [SettingsType.eventsQueueLockMode]: EventsQueueMode[value] },
      (t) =>
        t('SettingsStore.notifications.eventsQueueLockModeError', {
          ns: 'stores'
        })
    );
    if (!isError) {
      this.rootStore.appNotificationsStore.open({
        title: (t) =>
          t('SettingsStore.notifications.eventsQueueLockModeSuccess', {
            ns: 'stores'
          }),
        type: AppNotificationType.success
      });
    } else {
      this.eventsQueueMode = prevValue;
    }
  };

  updateThreshold = (value: number) => {
    this.debouncedLikeThreshold(this.likeThreshold || 0);
    this.likeThreshold = value;
  };

  updateTimezone = async (value: string) => {
    const prevValue = this.timezone;
    this.timezone = value;
    const { isError } = await this.update(
      { [SettingsType.timezone]: value },
      (t) =>
        t('SettingsStore.notifications.timezoneError', {
          ns: 'stores'
        })
    );
    if (!isError) {
      this.rootStore.projectStore.entity?.setTimezone(value);
      this.rootStore.appNotificationsStore.open({
        title: (t) =>
          t('SettingsStore.notifications.timezoneSuccess', {
            ns: 'stores'
          }),
        type: AppNotificationType.success
      });
    } else {
      this.timezone = prevValue;
    }
  };

  updateUserCardEmailVar = async (value: string | null) => {
    await this.saveUserCardVars(
      {
        email: value,
        phone: this.userCardPhoneVar
      },
      (t) =>
        t('SettingsStore.notifications.userCardEmailVarSuccess', {
          ns: 'stores'
        })
    );
  };

  updateUserCardPhoneVar = async (value: string | null) => {
    await this.saveUserCardVars(
      {
        email: this.userVars.email,
        phone: value
      },
      (t) =>
        t('SettingsStore.notifications.userCardPhoneVarSuccess', {
          ns: 'stores'
        })
    );
  };

  private saveUserCardVars = async (
    userVars: UserVars,
    successMessage: TranslationString
  ): Promise<void> => {
    if (this.updatingStage === LoadingStage.LOADING) {
      return;
    }

    const { email: prevEmail, phone: prevPhone } = this.userVars;

    this.userVars.email = userVars.email;
    this.userVars.phone = userVars.phone;
    this.updatingStage = LoadingStage.LOADING;

    const response = await this.rootStore.networkStore.api(
      apiUrls.PROJECTS_SET_SETTINGS,
      {
        method: 'POST',
        data: { user_vars: userVars },
        errorsMap: mapUserCardVarErrorToMessage
      }
    );

    runInAction(() => {
      if (response.isError) {
        this.userVars.email = prevEmail;
        this.userVars.phone = prevPhone;
        this.updatingStage = LoadingStage.ERROR;
        return;
      }

      this.rootStore.appNotificationsStore.open({
        title: successMessage,
        type: AppNotificationType.success
      });
      this.updatingStage = LoadingStage.SUCCESS;
    });
  };

  updateCsatEnabled = async (value: boolean): Promise<void> => {
    if (!this.csat) {
      return;
    }

    this.csat.changeEnabled(value);
    await this.updateCsat();
  };

  updateCsatAskComment = async (value: boolean): Promise<void> => {
    if (!this.csat) {
      return;
    }

    this.csat.askComment.changeValue(value);
    await this.updateCsat();
  };

  /** Сохранение текущего черновика CSAT — для commit-on-blur из prompt. */
  updateCsat = async (): Promise<void> => {
    const csat = this.csat;
    if (!csat?.canSave || this.updatingStage === LoadingStage.LOADING) {
      return;
    }

    this.updatingStage = LoadingStage.LOADING;
    await this.saveCsatPayload(csat, csat.toJSON());
  };

  private async saveCsatPayload(
    csat: CsatSettingsModel,
    payload: CsatSettingsServer,
    lastSuccessfulPayload: CsatSettingsServer | null = null
  ): Promise<void> {
    const response = await this.rootStore.networkStore.api(
      apiUrls.PROJECTS_SET_SETTINGS,
      {
        method: 'POST',
        data: { csat: payload }
      }
    );

    const currentPayload = csat.toJSON();

    const savedPayload = response.isError ? lastSuccessfulPayload : payload;

    if (csat.isValid && !areCsatSettingsEqual(currentPayload, payload)) {
      await this.saveCsatPayload(csat, currentPayload, savedPayload);
      return;
    }

    runInAction(() => {
      if (response.isError) {
        if (savedPayload) {
          csat.resetToPayload(savedPayload);
        } else {
          csat.reset();
        }
        this.updatingStage = LoadingStage.ERROR;
        this.rootStore.appNotificationsStore.open({
          title: (t) =>
            t('SettingsStore.notifications.csatError', { ns: 'stores' }),
          type: AppNotificationType.error
        });
        return;
      }

      if (areCsatSettingsEqual(currentPayload, payload)) {
        csat.resetTouchedValues();
      }
      this.rootStore.appNotificationsStore.open({
        title: (t) =>
          t('SettingsStore.notifications.csatSuccess', { ns: 'stores' }),
        type: AppNotificationType.success
      });
      this.updatingStage = LoadingStage.SUCCESS;
    });
  }

  /**
   * Откат черновика CSAT, который заведомо не сохранится: пустой текст запроса
   * блокирует updateCsat целиком, потому что toJSON() шлёт все три поля разом.
   * Валидный черновик не трогаем — он либо прямо сейчас летит на бэк, либо
   * уедет со следующим сохранением секции.
   */
  resetCsatDraft = (): void => {
    const csat = this.csat;
    if (!csat || !csat.touched || csat.isValid) {
      return;
    }

    csat.reset();
  };

  updateUnstopByManagerDelay = async (value: UnstopDelay) => {
    if (this.loadingStage !== LoadingStage.SUCCESS) {
      return;
    }

    const prevValue = this.unstopByManagerDelay.value;
    this.unstopByManagerDelay.changeValue(value);
    const { isError } = await this.update(
      { [SettingsType.unstopByManagerDelay]: value },
      (t) =>
        t('SettingsStore.notifications.unstopByManagerDelayError', {
          ns: 'stores'
        })
    );
    if (!isError) {
      this.rootStore.appNotificationsStore.open({
        title: (t) =>
          t('SettingsStore.notifications.unstopByManagerDelaySuccess', {
            ns: 'stores'
          }),
        type: AppNotificationType.success
      });
    } else {
      this.unstopByManagerDelay.changeValue(prevValue);
    }
  };

  updateConversationAutoCloseDelayMinutes = async (value: number | null) => {
    if (this.conversationAutoCloseUpdatingStage.isLoading) {
      return { isError: true };
    }

    if (value === this.conversationAutoCloseDelayMinutes) {
      return { isError: false };
    }

    const previousValue = this.conversationAutoCloseDelayMinutes;
    this.conversationAutoCloseDelayMinutes = value;
    this.conversationAutoCloseUpdatingStage.loading();
    const response = await this.rootStore.networkStore.api(
      apiUrls.PROJECTS_SET_SETTINGS,
      {
        method: 'POST',
        data: {
          [SettingsType.conversationAutoCloseDelayMinutes]: value
        }
      }
    );

    if (response.isError) {
      runInAction(() => {
        this.conversationAutoCloseDelayMinutes = previousValue;
        this.conversationAutoCloseUpdatingStage.error();
      });

      this.rootStore.appNotificationsStore.open({
        title: (t) =>
          t('SettingsStore.notifications.conversationAutoCloseDelayError', {
            ns: 'stores'
          }),
        type: AppNotificationType.error
      });

      return { isError: true };
    }

    this.conversationAutoCloseUpdatingStage.success();
    this.rootStore.appNotificationsStore.open({
      title: (t) =>
        t('SettingsStore.notifications.conversationAutoCloseDelaySuccess', {
          ns: 'stores'
        }),
      type: AppNotificationType.success
    });

    return { isError: false };
  };

  async update(
    params: Partial<ProjectSettingsServer>,
    errorMessage: TranslationString,
    successMessage?: TranslationString
  ): Promise<BaseResponse> {
    if (this.updatingStage === LoadingStage.LOADING) {
      return { isError: true };
    }

    this.updatingStage = LoadingStage.LOADING;

    const response = await this.rootStore.networkStore.api(
      apiUrls.PROJECTS_SET_SETTINGS,
      { method: 'POST', data: params }
    );

    runInAction(() => {
      if (response.isError) {
        this.rootStore.appNotificationsStore.open({
          title: errorMessage,
          type: AppNotificationType.error
        });
        this.updatingStage = LoadingStage.ERROR;
        return { isError: true };
      }

      if (successMessage) {
        this.rootStore.appNotificationsStore.open({
          title: successMessage,
          type: AppNotificationType.success
        });
      }

      this.updatingStage = LoadingStage.SUCCESS;
      return { isError: false };
    });
    return { isError: response.isError };
  }

  async load(): Promise<BaseResponse> {
    if (this.loadingStage === LoadingStage.LOADING || !this.canBeLoaded) {
      return { isError: true };
    }

    this.loadingStage = LoadingStage.LOADING;

    const response =
      await this.rootStore.networkStore.api<ProjectSettingsServer>(
        apiUrls.PROJECTS_LIST_SETTINGS
      );

    if (!response.isError) {
      runInAction(() => {
        this.debugMode = ModeProjectModel.fromJson({
          raw: response.data.debug_mode,
          settingsStore: this,
          type: SettingsType.debugMode
        });
        this.useUnpublished = ModeProjectModel.fromJson({
          raw: response.data.use_unpublished,
          settingsStore: this,
          type: SettingsType.useUnpublished
        });
        this.enabledProjectSetting = EnabledProjectSettingModel.fromJson({
          raw: response.data.bot_enabled,
          settingsStore: this
        });
        this.unstopByManagerDelay.changeValue(
          response.data.unstop_by_manager_delay
        );
        this.eventsQueueMode = response.data.events_queue_lock_mode;
        this.likeThreshold = response.data.like_threshold;
        this.timezone = response.data.timezone;
        this.userVars = normalizeUserVars(response.data.user_vars);
        this.csat = new CsatSettingsModel(
          normalizeCsatSettings(response.data.csat)
        );
        this.businessHours.setProjectSettings(response.data);
        this.businessHours.loadingStage.success();
        this.conversationAutoCloseDelayMinutes =
          response.data.conversation_auto_close_delay_minutes;
        this.loadingStage = LoadingStage.SUCCESS;
      });
    } else {
      runInAction(() => {
        this.loadingStage = LoadingStage.ERROR;
      });
    }

    return { isError: response.isError };
  }

  reset() {
    this.debugMode = null;
    this.useUnpublished = null;
    this.enabledProjectSetting = null;
    this.eventsQueueMode = null;
    this.likeThreshold = null;
    this.timezone = null;
    this.conversationAutoCloseDelayMinutes = null;
    this.conversationAutoCloseUpdatingStage.reset();
    this.businessHours.reset();
    this.unstopByManagerDelay.changeValue(null);
    this.userVars = {
      email: null,
      phone: null
    };
    this.csat = null;
    this.loadingStage = LoadingStage.NOT_STARTED;
    this.updatingStage = LoadingStage.NOT_STARTED;
  }
}
